import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Spin,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../../../contexts/LanguageContext';
import EFormPivotReport from './components/EFormPivotReport';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_ENDPOINTS = {
  pending: '/api/eforminstances/pending',
  approved: '/api/eforminstances/approved',
  rejected: '/api/eforminstances/rejected',
  manualPending: '/api/eforminstances/manual/pending',
  manualResponded: '/api/eforminstances/manual/responded',
};

const EFormInstancePivotPage = () => {
  const { t } = useLanguage();

  const [filters, setFilters] = useState({
    status: 'all',
    keyword: '',
    dateRange: null,
  });
  const [selectedFormNames, setSelectedFormNames] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formListFromApi, setFormListFromApi] = useState([]);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') || '全部' },
      { value: 'pending', label: t('common.pending') || '待處理' },
      { value: 'approved', label: t('common.approved') || '已批准' },
      { value: 'rejected', label: t('common.rejected') || '已拒絕' },
      { value: 'manualPending', label: t('reports.eformInstanceManualPending') || '待回應表單' },
      { value: 'manualResponded', label: t('reports.eformInstanceManualResponded') || '已收到回應表單' },
    ],
    [t]
  );

  const normalizeRecord = (record, sourceKey) => {
    let fieldDisplaySettings =
      record.fieldDisplaySettings ?? record.field_display_settings ?? null;
    if (typeof fieldDisplaySettings === 'string') {
      try {
        fieldDisplaySettings = JSON.parse(fieldDisplaySettings);
      } catch {
        fieldDisplaySettings = [];
      }
    }
    if (!Array.isArray(fieldDisplaySettings)) fieldDisplaySettings = [];

    return {
      ...record,
      id: record.id || record.Id,
      formName: record.formName || record.form_name,
      instanceName: record.instanceName || record.instance_name,
      status: record.status || record.formStatus || record.state || sourceKey,
      fillType: record.fillType || record.fill_type,
      createdAt: record.createdAt || record.created_at,
      fieldDisplaySettings,
      parsedFieldValues: record.parsedFieldValues ?? {},
      sourceKey,
    };
  };

  const buildQueryParams = (currentFilters) => {
    const params = new URLSearchParams({ page: '1', pageSize: '5000' });
    if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
      params.append('createdDateFrom', dayjs(currentFilters.dateRange[0]).toISOString());
      params.append('createdDateTo', dayjs(currentFilters.dateRange[1]).toISOString());
    }
    return params.toString();
  };

  const fetchByStatus = async (statusKey, currentFilters) => {
    const token = localStorage.getItem('token');
    const params = buildQueryParams(currentFilters);
    const endpoint = STATUS_ENDPOINTS[statusKey];
    if (!endpoint) return [];
    const response = await fetch(`${endpoint}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return (data.data || data || []).map((r) => normalizeRecord(r, statusKey));
  };

  const applyKeywordFilter = (records, keyword) => {
    if (!keyword) return records;
    const lowered = keyword.toLowerCase();
    return records.filter((record) => {
      const formName = (record.formName || '').toLowerCase();
      const instanceName = (record.instanceName || '').toLowerCase();
      if (formName.includes(lowered) || instanceName.includes(lowered)) return true;
      const parsedValues = record.parsedFieldValues || {};
      const visibleFields = (record.fieldDisplaySettings || []).filter((f) => f.showInList);
      for (const field of visibleFields) {
        const val = parsedValues[field.fieldId];
        if (val != null && String(val).toLowerCase().includes(lowered)) return true;
      }
      return false;
    });
  };

  const loadReportData = useCallback(
    async (currentFilters = filters) => {
      setLoading(true);
      try {
        const statusKeys =
          currentFilters.status === 'all'
            ? Object.keys(STATUS_ENDPOINTS)
            : [currentFilters.status];
        const results = await Promise.all(
          statusKeys.map((k) => fetchByStatus(k, currentFilters))
        );
        const merged = results.flat();
        const uniqueMap = new Map();
        merged.forEach((r) => {
          if (r.id && !uniqueMap.has(r.id)) uniqueMap.set(r.id, r);
        });
        const uniqueRecords = Array.from(uniqueMap.values()).sort((a, b) => {
          const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tB - tA;
        });
        const filtered = applyKeywordFilter(uniqueRecords, currentFilters.keyword);
        setDataSource(filtered);

        if (filtered.length === 0) {
          message.info(t('reports.eformInstancesNoData') || '沒有符合條件的表單資料');
        } else {
          message.success(
            (t('reports.totalRecords') || '共 {total} 條記錄').replace('{total}', filtered.length)
          );
        }
      } catch (error) {
        console.error('載入 eform pivot 報表失敗:', error);
        message.error(t('reports.loadDataFailed') || '載入報表數據失敗');
        setDataSource([]);
      } finally {
        setLoading(false);
      }
    },
    [t, filters]
  );

  useEffect(() => {
    const fetchFormList = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/eforms?page=1&pageSize=500', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        const list = json?.data || json || [];
        const options = list
          .map((f) => ({ name: f.Name || f.name, id: f.Id || f.id }))
          .filter((f) => f.name)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)))
          .map((f) => ({ label: f.name, value: f.name }));
        setFormListFromApi(options);
      } catch (err) {
        console.warn('載入表單清單失敗:', err);
      }
    };
    fetchFormList();
  }, []);

  const formNameOptions = useMemo(() => {
    if (formListFromApi.length > 0) return formListFromApi;
    const fromData = [...new Set((dataSource || []).map((r) => r.formName).filter(Boolean))].sort();
    return fromData.map((name) => ({ label: name, value: name }));
  }, [formListFromApi, dataSource]);

  const dataForPivot = useMemo(() => {
    if (!selectedFormNames || selectedFormNames.length === 0) return dataSource;
    return (dataSource || []).filter((r) => selectedFormNames.includes(r.formName));
  }, [dataSource, selectedFormNames]);

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>
              {t('reports.eformPivotTitle') || 'EForm 樞紐分析報表'}
            </h2>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              {t('reports.eformPivotDescription') ||
                '自訂度量與維度，對表單實例進行分組匯總分析'}
            </p>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadReportData(filters)}
            loading={loading}
          >
            {t('common.refresh') || '刷新'}
          </Button>
        </div>
      </Card>

      <Card
        title={t('reports.eformInstancesFilters') || '查詢條件'}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 8 }}>
              <Text>{t('reports.eformInstancesKeyword') || '關鍵字'}</Text>
            </div>
            <Input
              placeholder={
                t('reports.eformInstancesKeywordPlaceholder') ||
                '表單名稱 / 實例名稱 / 表單欄位內容'
              }
              value={filters.keyword}
              onChange={(e) => setFilters((prev) => ({ ...prev, keyword: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 8 }}>
              <Text>{t('reports.eformInstancesStatus') || '狀態'}</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
            >
              {statusOptions.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div style={{ marginBottom: 8 }}>
              <Text>{t('reports.dateRange') || '時間範圍'}</Text>
            </div>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(values) => setFilters((prev) => ({ ...prev, dateRange: values }))}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Col>
          <Col xs={24} sm={24} md={24}>
            <div style={{ marginBottom: 8 }}>
              <Text>{t('reports.pivotFilterByForms') || '選擇表單（可多選）'}</Text>
            </div>
            <Select
              mode="multiple"
              allowClear
              placeholder={t('reports.pivotFilterByFormsPlaceholder') || '不選＝全部表單'}
              style={{ width: '100%', minWidth: 200 }}
              value={selectedFormNames}
              onChange={setSelectedFormNames}
              options={formNameOptions}
              maxTagCount="responsive"
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button icon={<SearchOutlined />} type="primary" onClick={() => loadReportData(filters)} loading={loading}>
              {t('common.search') || '查詢'}
            </Button>
            <Button
              onClick={() => {
                setFilters({ status: 'all', keyword: '', dateRange: null });
                setSelectedFormNames([]);
                setDataSource([]);
              }}
            >
              {t('common.reset') || '重置'}
            </Button>
          </Space>
        </div>
      </Card>

      <Spin spinning={loading} tip={t('reports.loading') || '載入中...'}>
        <EFormPivotReport dataSource={dataForPivot} />
      </Spin>
    </div>
  );
};

export default EFormInstancePivotPage;
